import React, {
	memo,
	useCallback,
	useMemo,
	useState
} from 'react';
import { Tab } from 'semantic-ui-react';

import { log } from '../../../../../services/utils';

import {
	deleteCredential,
	getCredentialsList
} from '../../../../../api/nora/contacts';

import { useAuthState } from '../../../../../contexts/AuthContext';
import {
	useContactState,
	useContactAction
} from '../../../../../contexts/ContactsContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import Actions from '../../../../Shared/Actions';
import CredentialsModal from './CredentialsModal';
import DateCell from '../../.././../Table/Cells/DateCell';
import TableRowActions from '../../.././../Table/Cells/TableRowActions';
import MultiSelectColumnFilter from '../../.././../Table/ColumnFilters/MultiSelectColumnFilter';
import multiSelectFilterFn from '../../.././../Table/filterFunctions/multiSelectFilterFn';
import multiWordFilterFn from '../../../../Table/filterFunctions/multiWordFilterFn';
import PageHeader from '../../../../Shared/PageHeader';
import Table from '../../../../Table';
import useDeleteConfirm from '../../../../../hooks/useDeleteConfirm';

const CredentialsTab = () => {
	const { user: { permissions } } = useAuthState();
	const { contact } = useContactState();
	const { setCredentials } = useContactAction();
	const {
		setApiErrorAlert,
		setInfobarLoader
	} = useUIAction();

	const [ credential, setCredential ] = useState( null );

	const retrieveCredentialsList = useCallback( async () => {
		setInfobarLoader( 'Retrieving Credentials List...' );

		try {
			const data = await getCredentialsList( contact?.id );

			setCredentials( data );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		setInfobarLoader,
		contact,
		setCredentials,
		setApiErrorAlert
	] );

	const handleDeleteCredential = useCallback( async credential => {
		try {
			await deleteCredential( contact?.id, credential.id );
			await retrieveCredentialsList();
		} catch ( error ) {
			setApiErrorAlert( error );
		}
	}, [
		contact,
		retrieveCredentialsList,
		setApiErrorAlert
	] );

	const {
		DeleteConfirmAlert,
		setDeleteConfirm,
		setToDelete
	} = useDeleteConfirm( handleDeleteCredential );

	const completionDateCell = useCallback(
		( { row } ) => row.original.completionDate
			? <DateCell date={ row.original.completionDate } />
			: <span>N/A</span>
		, []
	);

	const expirationDateCell = useCallback(
		( { row } ) => row.original.expirationDate
			? <DateCell date={ row.original.expirationDate } />
			: <span>N/A</span>
		, []
	);

	const onEdit = useCallback( credential => {
		setCredential( credential );
	}, [] );

	const onDelete = useCallback( credential => {
		setToDelete( credential );
		setDeleteConfirm( <span>Are you sure you would like to delete <strong>{ credential.firstName } { credential.lastName }</strong> from the credential list?</span> );
	}, [
		setDeleteConfirm,
		setToDelete
	] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onEdit,
				componentProps: { color: 'pink' },
				disable: !permissions.editContacts.canExecute,
				icon: 'pencil',
				permission: permissions.editContacts.isAvailable,
				text: 'Edit Credential'
			},
			{
				action: onDelete,
				componentProps: { negative: true },
				disable: !permissions.editContacts.canExecute,
				icon: 'times',
				permission: permissions.editContacts.isAvailable,
				text: 'Delete Credential'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onDelete,
		onEdit,
		permissions
	] );

	const columns = useMemo( () => [
		{
			accessor: 'label',
			Header: 'Label',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			id: 'type',
			accessor: row => `${ row.type.name }`,
			Header: 'Type',
			Filter: MultiSelectColumnFilter,
			filter: multiSelectFilterFn,
			minWidth: 60
		},
		{
			accessor: 'completionDate',
			Header: 'Completion Date',
			filter: multiWordFilterFn,
			Cell: completionDateCell,
			minWidth: 60
		},
		{
			accessor: 'expirationDate',
			Header: 'Expiration Date',
			filter: multiWordFilterFn,
			Cell: expirationDateCell,
			minWidth: 60
		},
		{
			id: 'status',
			accessor: row => `${ row.status.name }`,
			Header: 'Status',
			Filter: MultiSelectColumnFilter,
			filter: multiSelectFilterFn,
			minWidth: 60
		},
		{
			id: 'id_actions',
			Cell: tableRowActionsCell,
			disableFilters: true,
			disableResizing: true,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		}
	], [
		completionDateCell,
		expirationDateCell,
		tableRowActionsCell
	] );

	const addCredential = useCallback( () => {
		setCredential( {
			id: 0,
			type: { id: 0 }
		} );
	}, [] );

	const actions = useMemo( () => [
		{
			action: addCredential,
			disable: !permissions.editContacts.canExecute,
			icon: 'add',
			permission: permissions.editContacts.isAvailable,
			text: 'Add Credential'
		}
	], [
		addCredential,
		permissions
	] );

	const closeCredentialsModal = useCallback( () => {
		setCredential( null );
	}, [] );

	return (
		<Tab.Pane as="div">
			{ log( 'CredentialsTable' ) }
			<Actions />
			<PageHeader
				actions={ actions }
				className="table-page-header"
				title="Credentials"
			/>
			<div className="table-container">
				{ contact?.credentials.length
					? <Table
						columns={ columns }
						data={ contact.credentials }
						showCount={ contact.credentials.length > 10 }
						showEmptyRows={ false }
						showPagination={ contact.credentials.length > 10 }
					/>
					: <p>No records found.</p>
				}
			</div>
			<CredentialsModal
				closeModal={ closeCredentialsModal }
				credential={ credential }
				open={ Boolean( credential ) }
				retrieveCredentialsList={ retrieveCredentialsList }
			/>
			<DeleteConfirmAlert />
		</Tab.Pane>
	);
};

export default memo( CredentialsTab );
